import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, ListGroup, Badge, InputGroup, Row, Col, Alert, Spinner } from 'react-bootstrap';
import blogService from '../../../services/blogService';

interface Category {
    id: number;
    name: string;
    slug: string;
    description?: string;
    postCount?: number;
    post_count?: number;
}

interface Tag {
    id: number;
    name: string;
    slug: string;
    postCount?: number;
    post_count?: number;
}

interface CategoryTagManagerProps {
    show: boolean;
    onHide: () => void;
    type: 'category' | 'tag';
}

const CategoryTagManager: React.FC<CategoryTagManagerProps> = ({ show, onHide, type }) => {
    const [items, setItems] = useState<(Category | Tag)[]>([]);
    const [showAddModal, setShowAddModal] = useState(false);
    const [editingItem, setEditingItem] = useState<Category | Tag | null>(null);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const title = type === 'category' ? 'Categories' : 'Tags';
    const singular = type === 'category' ? 'Category' : 'Tag';

    useEffect(() => {
        if (show) {
            loadItems();
        }
    }, [type, show]);

    const loadItems = async () => {
        setLoading(true);
        setError(null);
        try {
            if (type === 'category') {
                const response = await blogService.getAllCategories();
                if (response.success) {
                    setItems(response.data);
                }
            } else {
                const response = await blogService.getAllTags();
                if (response.success) {
                    setItems(response.data);
                }
            }
        } catch (err: any) {
            console.error('Error loading items:', err);
            setError(err.message || `Failed to load ${title.toLowerCase()}`);
        } finally {
            setLoading(false);
        }
    };

    const generateSlug = (text: string) => {
        return text
            .toLowerCase()
            .replace(/[^\w\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
            .trim();
    };

    const handleAddEdit = async () => {
        if (!name.trim()) return;

        const slug = generateSlug(name);
        setSaving(true);
        setError(null);

        try {
            if (editingItem) {
                // Edit existing
                if (type === 'category') {
                    await blogService.updateCategory(editingItem.id, name.trim(), slug, description.trim());
                } else {
                    await blogService.updateTag(editingItem.id, name.trim(), slug);
                }
            } else {
                // Add new
                if (type === 'category') {
                    await blogService.createCategory(name.trim(), slug, description.trim());
                } else {
                    await blogService.createTag(name.trim(), slug);
                }
            }

            // Reload items after successful save
            await loadItems();
            resetForm();
        } catch (err: any) {
            console.error('Error saving item:', err);
            setError(err.message || `Failed to save ${singular.toLowerCase()}`);
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (item: Category | Tag) => {
        setEditingItem(item);
        setName(item.name);
        if (type === 'category' && 'description' in item) {
            setDescription(item.description || '');
        }
        setShowAddModal(true);
    };

    const handleDelete = async (id: number) => {
        const item = items.find(i => i.id === id);
        const postCount = item?.postCount || item?.post_count || 0;

        if (item && postCount > 0) {
            if (!window.confirm(`This ${singular.toLowerCase()} is used in ${postCount} post(s). Are you sure you want to delete it?`)) {
                return;
            }
        } else {
            if (!window.confirm(`Are you sure you want to delete this ${singular.toLowerCase()}?`)) {
                return;
            }
        }

        setError(null);
        try {
            if (type === 'category') {
                await blogService.deleteCategory(id);
            } else {
                await blogService.deleteTag(id);
            }

            // Reload items after successful deletion
            await loadItems();
        } catch (err: any) {
            console.error('Error deleting item:', err);
            setError(err.message || `Failed to delete ${singular.toLowerCase()}`);
        }
    };

    const resetForm = () => {
        setName('');
        setDescription('');
        setEditingItem(null);
        setShowAddModal(false);
    };

    const filteredItems = items.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <>
            <Modal show={show} onHide={onHide} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Manage {title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {error && (
                        <Alert variant="danger" dismissible onClose={() => setError(null)}>
                            <i className="fa fa-exclamation-circle me-2"></i>
                            {error}
                        </Alert>
                    )}

                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <InputGroup style={{ maxWidth: '300px' }}>
                            <InputGroup.Text>
                                <i className="fa fa-search"></i>
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder={`Search ${title.toLowerCase()}...`}
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                        <Button variant="primary" onClick={() => setShowAddModal(true)} disabled={loading}>
                            <i className="fa fa-plus me-2"></i>Add {singular}
                        </Button>
                    </div>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <p className="mt-2 text-muted">Loading {title.toLowerCase()}...</p>
                        </div>
                    ) : filteredItems.length === 0 ? (
                        <Alert variant="info" className="text-center">
                            <i className="fa fa-info-circle me-2"></i>
                            No {title.toLowerCase()} yet. Click "Add {singular}" to create one.
                        </Alert>
                    ) : (
                        <ListGroup>
                            {filteredItems.map((item) => {
                                const postCount = item.postCount || item.post_count || 0;
                                return (
                                    <ListGroup.Item
                                        key={item.id}
                                        className="d-flex justify-content-between align-items-start"
                                    >
                                        <div className="flex-grow-1">
                                            <div className="d-flex align-items-center mb-1">
                                                <strong className="me-2">{item.name}</strong>
                                                <Badge bg="secondary" className="me-2">
                                                    {postCount} {postCount === 1 ? 'post' : 'posts'}
                                                </Badge>
                                                <small className="text-muted">/{item.slug}</small>
                                            </div>
                                            {type === 'category' && 'description' in item && item.description && (
                                                <small className="text-muted">{item.description}</small>
                                            )}
                                        </div>
                                        <div className="d-flex gap-2">
                                            <Button
                                                size="sm"
                                                variant="outline-primary"
                                                onClick={() => handleEdit(item)}
                                            >
                                                <i className="fa fa-edit"></i>
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                onClick={() => handleDelete(item.id)}
                                            >
                                                <i className="fa fa-trash"></i>
                                            </Button>
                                        </div>
                                    </ListGroup.Item>
                                );
                            })}
                        </ListGroup>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={onHide}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Add/Edit Modal */}
            <Modal show={showAddModal} onHide={resetForm}>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingItem ? `Edit ${singular}` : `Add New ${singular}`}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control
                            type="text"
                            placeholder={`Enter ${singular.toLowerCase()} name`}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            autoFocus
                        />
                        {name && (
                            <Form.Text className="text-muted">
                                Slug: /{generateSlug(name)}
                            </Form.Text>
                        )}
                    </Form.Group>

                    {type === 'category' && (
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                placeholder="Brief description (optional)"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </Form.Group>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={resetForm} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleAddEdit}
                        disabled={!name.trim() || saving}
                    >
                        {saving ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <i className="fa fa-save me-2"></i>
                                {editingItem ? 'Update' : 'Create'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CategoryTagManager;
